import { ApiErrorDisplay } from '@/types/apiError';
import { InfoOutline } from '@mui/icons-material';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { Box, Chip, CircularProgress, FormControlLabel, IconButton, Radio, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { MatchType } from '../types';

const TYPE_SELECTION_SIZE = 14;

const CardContent = styled(Box, {
  shouldForwardProp: property => property !== 'selected' && property !== 'isBeta' && property !== 'disabled',
})<{ selected: boolean; isBeta?: boolean; disabled?: boolean }>(({ theme, selected, disabled }) => ({
  flex: 1,
  padding: '7.5px 8px',
  borderRadius: '6px',
  backgroundColor: selected ? theme.palette.secondary.main : theme.palette.background.paper,
  border: `1px solid ${selected ? theme.palette.secondary.main : theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
  cursor: disabled ? 'default' : 'pointer',
  transition: 'all 0.2s ease',
  opacity: disabled ? 0.5 : 1,
  '&:hover': {
    border: disabled ? `1px solid ${theme.palette.divider}` : `1px solid ${theme.palette.secondary.main}`,
  },
}));

const TooltipContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const TooltipImage = styled('img')(({ theme }) => ({
  maxWidth: 300,
  height: 220,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: '2px',
}));

const TooltipDescription = styled(Typography)(() => ({
  textAlign: 'center',
  maxWidth: 250,
  paddingBottom: '4px',
}));

export const MatchTypeRadio = ({
  value,
  label,
  count,
  imageSrc,
  description,
  selected,
  isLoading,
  error,
  onChange,
  disabled,
  beta,
}: {
  value: MatchType;
  label: string;
  count?: number;
  imageSrc: string;
  description: string;
  selected: boolean;
  isLoading?: boolean;
  error?: ApiErrorDisplay;
  onChange: (value: MatchType) => void;
  disabled?: boolean;
  beta?: boolean;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
      <CardContent selected={selected} isBeta={beta} disabled={disabled} onClick={() => !disabled && onChange(value)}>
        <FormControlLabel
          value={value}
          control={<Radio size='small' sx={{ padding: '4px', display: 'none' }} />}
          label={
            <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
              <Box display='flex' alignItems='center' gap={0.25}>
                <Typography
                  sx={{
                    fontSize: TYPE_SELECTION_SIZE,
                    color: selected ? 'white' : 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
                <Tooltip
                  title={
                    <TooltipContent display='flex' flexDirection='column'>
                      <TooltipImage src={imageSrc} alt={`${label} Match Example`} />
                      <TooltipDescription variant='body2' color='text.white'>
                        {description}
                      </TooltipDescription>
                    </TooltipContent>
                  }
                  open={isTooltipOpen || isHovered}
                  onClose={() => {
                    setIsTooltipOpen(false);
                    setIsHovered(false);
                  }}
                >
                  <IconButton
                    size='small'
                    onClick={event => {
                      event.stopPropagation();
                      setIsTooltipOpen(!isTooltipOpen);
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    sx={{
                      padding: '2px',
                      minWidth: '16px',
                      minHeight: '16px',
                      marginLeft: '2px',
                      color: selected ? 'white' : 'text.secondary',
                    }}
                  >
                    <InfoOutline sx={{ fontSize: '14px' }} />
                  </IconButton>
                </Tooltip>
                {beta && (
                  <Chip
                    label='Beta'
                    size='small'
                    icon={<AutoAwesome sx={{ fontSize: '10px !important' }} />}
                    sx={{
                      height: '16px',
                      fontSize: '9px',
                      fontWeight: 600,
                      padding: '0 4px',
                      marginLeft: '4px',
                      background: selected
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'linear-gradient(135deg, rgb(94, 160, 129) 0%, rgb(76, 133, 102) 25%, rgb(67, 115, 93) 50%, rgb(76, 133, 102) 75%, rgb(85, 151, 120) 100%)',
                      color: 'white',
                      '& .MuiChip-icon': {
                        color: 'white',
                        marginLeft: '4px',
                        marginRight: '2px',
                      },
                      '& .MuiChip-label': {
                        padding: '0 4px 0 0',
                      },
                    }}
                  />
                )}
              </Box>
              {isLoading ? (
                <CircularProgress size={TYPE_SELECTION_SIZE} sx={{ color: selected ? 'white' : undefined }} />
              ) : (
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: TYPE_SELECTION_SIZE,
                    justifySelf: 'flex-end',
                    marginRight: '4px',
                    color: selected ? 'white' : 'text.secondary',
                  }}
                >
                  {error ? '' : `(${count})`}
                </Typography>
              )}
            </Box>
          }
          sx={{
            marginRight: 0,
            marginBottom: '-4px',
            flex: 1,
            alignItems: 'center',
            width: '100%',
            margin: 0,
            '& .MuiFormControlLabel-label': {
              width: '100%',
            },
            '& .MuiRadio-root': {
              color: selected ? 'white' : undefined,
            },
            '& .Mui-checked': {
              color: selected ? 'white' : undefined,
            },
          }}
          checked={selected}
          onChange={() => onChange(value)}
          disabled={disabled}
        />
      </CardContent>
    </Box>
  );
};
